import { useFetcher, useLocation, useNavigate } from '@remix-run/react'
import { type ReactElement, useEffect, useState } from 'react'
import { CardDetails, CardList, EmptyCard } from '~/design-system/components'
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeading,
  Loading,
  Modal,
} from '~/design-system/primitives'
import { useGlobalToast } from '~/hooks/useGlobalToast'
import { useScrollToTop } from '~/hooks/useScrollToTop'
import { cn } from '~/lib/cn'
import type { AvailableAccount } from '~/type/Cart'
import 'react-credit-cards-2/dist/es/styles-compiled.css'

interface MyCardsProps {
  cardHolder: string
  documentHolder: string
}

interface CardData {
  cardNumber: string
  expirationDate: string
  documentHolder: string
  accountId: string
}

interface ResponseCard {
  response: any
  success: boolean
}

interface ResponseDataAvailableAccount {
  response: AvailableAccount[]
  success: boolean
}

export default function MyCards({ cardHolder, documentHolder }: MyCardsProps) {
  const fetcher = useFetcher()
  const fetcherGet = useFetcher()
  const navigate = useNavigate()
  const location = useLocation()
  const { show } = useGlobalToast()
  const [selectedComponent, setSelectedComponent] = useState<ReactElement | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showCardDetails, setShowCardDetails] = useState<boolean>(false)
  const [cardData, setCardData] = useState<CardData>({
    cardNumber: '',
    expirationDate: '',
    documentHolder: '',
    accountId: '',
  })

  useEffect(() => {
    const isDetailRoute = location.pathname.includes('/detalhe-cartao')
    setShowCardDetails(isDetailRoute)
  }, [location.pathname])

  useScrollToTop({ isLoading })

  const fetcherGetListAvailableAccounts = () => {
    fetcherGet.submit({ actionType: 'getListAvaliableAccounts' }, { method: 'post', action: '/myaccount' })
  }

  useEffect(() => {
    setIsLoading(true)
    fetcherGetListAvailableAccounts()
  }, [])

  useEffect(() => {
    const dataAvailableAccount = fetcherGet.data as ResponseDataAvailableAccount

    if (dataAvailableAccount && dataAvailableAccount.response) {
      setIsLoading(false)
      setSelectedComponent(
        dataAvailableAccount.response.length === 0 ? (
          <EmptyCard />
        ) : (
          <CardList
            availableAccounts={dataAvailableAccount.response}
            getCardDetails={getCardDetails}
            verifyToDeleteCard={verifyToDeleteCard}
          />
        ),
      )
    }
  }, [fetcherGet.data, fetcher.data])

  useEffect(() => {
    const dataDeleteCard = fetcher.data as ResponseCard

    if (dataDeleteCard && dataDeleteCard.response) {
      if (dataDeleteCard?.success) {
        show({ message: 'Cartão excluido com sucesso', type: 'success' })
      } else {
        show({ message: 'Não foi possivel excluir o cartão', type: 'error' })
      }

      setIsLoading(false)
      setShowCardDetails(false)
      fetcherGetListAvailableAccounts()
    }
  }, [fetcher.data])

  function maskDocument(documentHolder: string): string {
    if (documentHolder.length == 11) {
      return documentHolder.replace(/\d(?=\d{3})/g, '*')
    }
    if (documentHolder.length == 14) {
      return documentHolder.replace(/\d(?=\d{4})/g, '*')
    }
    return documentHolder
  }

  function getCardDetails(creditCard: AvailableAccount): void {
    setCardData({
      cardNumber: creditCard.cardNumber,
      expirationDate: creditCard.expirationDate,
      documentHolder: maskDocument(documentHolder),
      accountId: creditCard.accountId,
    })
    navigate(`/myaccount/meus-cartoes/detalhe-cartao`)
  }

  function handleBackToList() {
    navigate('/myaccount/meus-cartoes')
  }

  function verifyToDeleteCard(accountId: string): void {
    setShowModal(true)
    setCardData((prevCardData) => ({
      ...prevCardData,
      accountId,
    }))
  }

  function deleteCard(idCard: string): void {
    if (idCard) {
      fetcher.submit({ idCard, actionType: 'deleteCard' }, { method: 'post', action: '/myaccount' })
    }
    setShowModal(false)
    setIsLoading(true)
  }

  return (
    <>
      <div className="flex w-full flex-col sm:mainContent">
        {showCardDetails && (
          <div className="mb-4 flex items-center space-x-2 text-sm text-gray-600">
            <span className="cursor-pointer hover:underline font-medium" onClick={handleBackToList}>
              Meus Cartões
            </span>
            <span className="cursor-default text-gray-400">&gt;</span>
            <span className="cursor-default font-medium text-gray-800">Detalhe do cartão</span>
          </div>
        )}
        <Card className={cn('lg:w-6/6 self-stretch')} color="primary" border>
          {showCardDetails ? (
            <>
              <CardDetails
                cardData={cardData}
                cardHolder={cardHolder}
                documentHolder={documentHolder}
                verifyToDeleteCard={verifyToDeleteCard}
              />
            </>
          ) : (
            <>{selectedComponent}</>
          )}
        </Card>
      </div>
      {showModal && (
        <Modal className="bg-transparent max-w-[328px]" defaultOpen>
          <Dialog className="bg-white rounded-md max-w-[328px] pt-2.5 pr-5 pl-7">
            {({ close }) => (
              <DialogContent className="h-[70vhh] pt-3">
                <DialogHeading className="pb-0" slot="title">
                  <div className="flex items-center justify-start w-full mb-1">
                    <p className="text-primary-black text-lg font-semibold">Excluir Cartão </p>
                  </div>
                </DialogHeading>

                <div className="">
                  <DialogBody className="mt-3">
                    <p className="text-primary-black text-sm font-normal">
                      Você tem certeza de que deseja excluir este cartão de crédito? Esta ação não pode ser
                      desfeita.
                    </p>

                    <div className="mt-6 gap-3 grid grid-cols-2">
                      <Button
                        className="border border-gray-40 "
                        onPress={() => {
                          setShowModal(false)
                          close()
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="bg-primary-orange text-white"
                        onPress={() => deleteCard(cardData.accountId)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </DialogBody>
                </div>
              </DialogContent>
            )}
          </Dialog>
        </Modal>
      )}
      {isLoading && <Loading />}
    </>
  )
}
